import { useState } from "react";
import type { ErrorInfo } from "react";
import { Check, Copy } from "lucide-react";
import { usePage } from "@inertiajs/react";

interface Props {
  error: Error;
  errorInfo: ErrorInfo | null;
  onReset?: () => void;
}

export function ErrorDisplay({ error, errorInfo, onReset }: Props) {
  const [copied, setCopied] = useState(false);
  const page = usePage<{ appRevision?: string; railsStage?: string }>();
  const appRevision = page.props.appRevision;

  const report = [
    "## React Error Details",
    "",
    "### Error Message",
    "```",
    error.toString(),
    "```",
    "",
    "### Stack Trace",
    "```",
    error.stack || "",
    "```",
    "",
    "### Component Stack",
    "```",
    errorInfo?.componentStack || "Not available",
    "```",
    "",
    "### Environment",
    `- URL: ${window.location.href}`,
    `- Timestamp: ${new Date().toISOString()}`,
    `- User Agent: ${navigator.userAgent}`,
    `- Revision: ${appRevision}`,
  ].join("\n");

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-white via-[#f8f7f2] to-[#eef3f0] text-gray-900">
      <div className="mx-auto flex min-h-screen max-w-2xl items-center px-6 py-16">
        <section className="relative w-full rounded-3xl border border-gray-200/80 bg-white/90 p-8 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur">
          {appRevision && (
            <span className="absolute right-6 top-6 text-[10px] text-gray-300">
              {appRevision}
            </span>
          )}
          <p className="text-xs uppercase tracking-[0.3em] text-gray-400">
            Development Error
          </p>
          <h1 className="mt-3 text-2xl font-semibold leading-tight text-gray-900 sm:text-3xl">
            {error.name}
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">
            {error.message}
          </p>
          <div className="mt-8 h-px w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
          <div className="mt-6 text-sm text-gray-500">
            状況が続く場合は、詳細をコピーして共有してください。
          </div>

          <details
            open
            className="mt-6 rounded-2xl border border-gray-200 bg-gray-50/80 p-4 text-xs text-gray-700"
          >
            <summary className="cursor-pointer text-sm font-semibold text-gray-800">
              詳細を表示
            </summary>
            <div className="mt-3 space-y-3">
              <div className="flex justify-end gap-2">
                {onReset && (
                  <button
                    onClick={onReset}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                  >
                    リセット
                  </button>
                )}
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                >
                  {copied ? (
                    <>
                      <Check className="h-3.5 w-3.5 text-green-600" />
                      コピー完了
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" />
                      /llms.txt 形式でコピー
                    </>
                  )}
                </button>
              </div>

              <pre className="whitespace-pre-wrap rounded-lg bg-white/80 p-3 text-[11px] leading-relaxed text-red-700">
                {error.toString()}
              </pre>

              {error.stack && (
                <pre className="whitespace-pre-wrap rounded-lg bg-white/80 p-3 text-[11px] leading-relaxed text-gray-600">
                  {error.stack}
                </pre>
              )}

              {errorInfo?.componentStack && (
                <pre className="whitespace-pre-wrap rounded-lg bg-white/80 p-3 text-[11px] leading-relaxed text-gray-600">
                  {errorInfo.componentStack}
                </pre>
              )}
            </div>
          </details>
        </section>
      </div>
    </main>
  );
}
